import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Minus, ShoppingBag, Search, Truck, PackageCheck } from 'lucide-react';
import PublicLayout from '../../components/shared/PublicLayout';
import { useCart } from '../../contexts/CartContext';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiBase } from '../../lib/api';
import { getPublicImageUrl } from '../../lib/media';

const API = getApiBase();

export default function MenuPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [search, setSearch] = useState('');
  const [settings, setSettings] = useState(null);
  const [formulaDish, setFormulaDish] = useState(null);
  const [formulaSelections, setFormulaSelections] = useState({});
  const [searchParams] = useSearchParams();
  const orderMode = location.pathname === '/commander' || searchParams.get('order') === 'true';
  const { addItem, orderType, setOrderType, itemCount } = useCart();
  const priceKey = orderType === 'dine_in' ? 'dine_in' : orderType === 'takeaway' ? 'takeaway' : 'delivery';
  const categoryVisibilityKey = orderType === 'dine_in' ? 'visible_dine_in' : orderType === 'takeaway' ? 'visible_takeaway' : 'visible_delivery';
  const activeCategories = categories.filter(c => c.active !== false && c[categoryVisibilityKey] !== false);
  const orderTypeOptions = [
    settings?.takeaway_enabled !== false ? {
      key: 'takeaway',
      label: 'A emporter',
      description: 'Retrait rapide au restaurant',
      icon: PackageCheck,
    } : null,
    settings?.delivery_enabled !== false ? {
      key: 'delivery',
      label: 'Livraison',
      description: 'Commande livree a votre adresse',
      icon: Truck,
    } : null,
  ].filter(Boolean);
  const filtered = dishes.filter(d =>
    (!activeCategory || d.category_id === activeCategory) &&
    activeCategories.some(category => category.id === d.category_id) &&
    (!search || d.name.toLowerCase().includes(search.toLowerCase()))
  );

  useEffect(() => {
    if (!orderMode || location.pathname === '/menu') {
      navigate('/commander', { replace: true });
    }
  }, [navigate, orderMode, location.pathname]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/settings`),
      axios.get(`${API}/api/categories`),
      axios.get(`${API}/api/dishes?available_only=true`)
    ]).then(([settingsRes, catRes, dishRes]) => {
      setSettings(settingsRes.data);
      setCategories(catRes.data);
      setDishes(dishRes.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!activeCategories.length) return;
    const stillExists = activeCategories.some(category => category.id === activeCategory);
    if (!stillExists) {
      setActiveCategory(activeCategories[0].id);
    }
  }, [activeCategories, activeCategory]);

  useEffect(() => {
    if (!settings || !orderMode) return;
    const availableModes = [];
    if (settings.delivery_enabled !== false) availableModes.push('delivery');
    if (settings.takeaway_enabled !== false) availableModes.push('takeaway');
    if (!availableModes.length) return;
    if (!availableModes.includes(orderType)) {
      setOrderType(availableModes[0]);
    }
  }, [settings, orderMode, orderType, setOrderType]);

  const getCategoryDisplayName = (name) => {
    const normalized = (name || '').trim().toLowerCase();
    const labels = {
      'sandwichs & formules sandwichs': 'Sandwichs',
      'nos assiettes': 'Assiettes',
      "nos hors d'oeuvres froids": 'Entrees froides',
      "nos hors d'oeuvres chauds": 'Entrees chaudes',
      'menu enfant': 'Menu enfant',
      'nos formules': 'Formules maison',
      'formules': 'Formules',
      'suppléments': 'Supplements',
      'suppliments': 'Supplements',
      'boissons': 'Boissons',
      'dessert': 'Desserts',
      'desserts': 'Desserts',
      'formules mezzés': 'Formules mezzes',
      'formules mezzes': 'Formules mezzes',
    };
    return labels[normalized] || name;
  };

  const handleAdd = (dish) => {
    if (settings?.exceptional_closure_enabled && orderMode) {
      toast.error(settings.exceptional_closure_message || 'Le restaurant est temporairement ferme');
      return;
    }
    if (dish.dish_type === 'formula' && (dish.formula_groups || []).length > 0) {
      const emptyGroup = (dish.formula_groups || []).find(group => getDishesForGroup(group).length === 0);
      if (emptyGroup) {
        toast.error(`La formule "${dish.name}" n'est pas encore configuree: aucun choix disponible pour "${emptyGroup.name}"`);
        return;
      }
      const initialSelections = {};
      dish.formula_groups.forEach(group => {
        initialSelections[group.id] = [];
      });
      setFormulaSelections(initialSelections);
      setFormulaDish(dish);
      return;
    }
    addItem(dish, 1, [], priceKey);
    toast.success(`${dish.name} ajoute au panier`);
  };

  const getDishesForGroup = (group) => {
    const explicitDishIds = new Set(group.source_dish_ids || []);
    if (explicitDishIds.size > 0) {
      return dishes.filter(d => explicitDishIds.has(d.id) && d.available);
    }
    return dishes.filter(d => group.source_category_ids?.includes(d.category_id) && d.available);
  };

  const getFormulaChoiceCount = (groupId, optionId) => (
    (formulaSelections[groupId] || []).filter(item => item.id === optionId).length
  );

  const getIncludedSelectionCount = (group) => Math.max(0, Number(group.selection_max) || 0);

  const getOptionConfiguredExtra = (group, option) => Number((group.extra_prices || {})[option.id] || 0);

  const getSelectionCharge = (group, item, selectionIndex) => {
    if (selectionIndex < getIncludedSelectionCount(group)) {
      return getOptionConfiguredExtra(group, item);
    }
    return Number(item.prices?.[priceKey] || 0);
  };

  const getGroupSurchargeTotal = (group) => (
    (formulaSelections[group.id] || []).reduce(
      (sum, item, index) => sum + getSelectionCharge(group, item, index),
      0
    )
  );

  const getOptionExtraCount = (group, optionId) => (
    (formulaSelections[group.id] || []).reduce((count, item, index) => {
      if (item.id === optionId && index >= getIncludedSelectionCount(group)) {
        return count + 1;
      }
      return count;
    }, 0)
  );

  const addFormulaChoice = (group, option) => {
    setFormulaSelections(prev => {
      const selected = prev[group.id] || [];
      if (selected.length >= 20) {
        toast.error(`Maximum 20 choix pour ${group.name}`);
        return prev;
      }
      return { ...prev, [group.id]: [...selected, option] };
    });
  };

  const removeFormulaChoice = (group, option) => {
    setFormulaSelections(prev => {
      const selected = prev[group.id] || [];
      const removeIndex = selected.findIndex(item => item.id === option.id);
      if (removeIndex === -1) return prev;
      return {
        ...prev,
        [group.id]: selected.filter((_, index) => index !== removeIndex),
      };
    });
  };

  const confirmFormula = () => {
    if (!formulaDish) return;
    const invalidGroup = formulaDish.formula_groups.find(group => {
      const count = (formulaSelections[group.id] || []).length;
      return count < group.selection_min;
    });
    if (invalidGroup) {
      toast.error(`Complete la formule: ${invalidGroup.name}`);
      return;
    }
    const surchargeTotal = formulaDish.formula_groups.reduce((sum, group) => {
      return sum + getGroupSurchargeTotal(group);
    }, 0);
    addItem(
      formulaDish,
      1,
      [],
      priceKey,
      {
        displayName: formulaDish.name,
        priceOverride: (formulaDish.prices?.[priceKey] || 0) + surchargeTotal,
        surchargeTotal,
        vatRate: formulaDish.vat_rate,
        formulaSelections: formulaDish.formula_groups.map(group => ({
          group_id: group.id,
          group_name: group.name,
          items: (formulaSelections[group.id] || []).map((item, index) => ({
            id: item.id,
            name: item.name,
            extra_price: getSelectionCharge(group, item, index),
          })),
        })),
      }
    );
    setFormulaDish(null);
    toast.success(`${formulaDish.name} ajoute au panier`);
  };

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-medium mb-3">
            {orderMode ? 'Commander' : 'Decouvrez'}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2A2A28] tracking-tighter">
            {orderMode ? 'Commander en ligne' : 'Notre Carte'}
          </h1>
          {settings?.exceptional_closure_enabled && orderMode && (
            <div className="mt-4 inline-flex rounded-2xl border border-[#8C3A3A]/20 bg-[#8C3A3A]/8 px-4 py-3 text-sm text-[#8C3A3A]">
              {settings.exceptional_closure_message}
            </div>
          )}
        </div>

        {/* Order Type Selector */}
        {orderMode && (
          <div className="mx-auto mb-8 max-w-3xl" data-testid="order-type-selector">
            <div className="rounded-[30px] border border-[#EBE8E0] bg-white/90 p-3 shadow-[0_14px_40px_rgba(0,0,0,0.04)]">
              <div className="grid gap-3 sm:grid-cols-2">
                {orderTypeOptions.map((t) => {
                  const active = orderType === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setOrderType(t.key)}
                      className={`rounded-[24px] border px-5 py-4 text-left transition-all ${
                        active
                          ? 'border-[#5B6B3F] bg-[#5B6B3F] text-white shadow-[0_14px_30px_rgba(91,107,63,0.24)]'
                          : 'border-[#EBE8E0] bg-[#FAF8F5] text-[#2A2A28] hover:border-[#5B6B3F] hover:bg-white'
                      }`}
                      data-testid={`order-type-${t.key}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 rounded-2xl p-2 ${active ? 'bg-white/14' : 'bg-white border border-[#EBE8E0]'}`}>
                          <t.icon className={`h-5 w-5 ${active ? 'text-white' : 'text-[#5B6B3F]'}`} />
                        </div>
                        <div className="min-w-0">
                          <p className={`text-lg font-semibold ${active ? 'text-white' : 'text-[#2A2A28]'}`}>{t.label}</p>
                          <p className={`text-sm ${active ? 'text-white/85' : 'text-[#7A7A75]'}`}>{t.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {orderTypeOptions.length === 0 && (
                <div className="mt-3 rounded-2xl border border-[#8C3A3A]/20 bg-[#8C3A3A]/8 px-4 py-3 text-sm text-[#8C3A3A]">
                  Aucun mode de commande n'est disponible pour le moment.
                </div>
              )}
              <div className="mt-3 rounded-2xl bg-[#F6F2E8] px-4 py-3 text-sm text-[#5B6B3F]">
                <span className="font-semibold">Mode selectionne :</span>{' '}
                {orderType === 'delivery'
                  ? 'Livraison a domicile'
                  : 'Commande a emporter au restaurant'}
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mx-auto mb-8 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A7A75]" />
          <input
            type="text"
            placeholder="Rechercher un plat..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#EBE8E0] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#5B6B3F]/20 focus:border-[#5B6B3F]"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="menu-search"
          />
        </div>

        {/* Categories */}
        <div className="mb-8" data-testid="category-filter">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-medium">Categories</p>
              <p className="text-sm text-[#7A7A75]">
                {activeCategory
                  ? `Selection: ${getCategoryDisplayName(activeCategories.find(cat => cat.id === activeCategory)?.name || '')}`
                  : 'Parcourez toute la carte'}
              </p>
            </div>
          </div>
          <div className="rounded-[28px] border border-[#EBE8E0] bg-white/90 p-3 shadow-[0_10px_30px_rgba(0,0,0,0.03)]">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {activeCategories.map(cat => (
                <Button
                  key={cat.id}
                  variant={activeCategory === cat.id ? 'default' : 'outline'}
                  className={`h-auto min-h-[52px] rounded-2xl px-4 py-3 text-sm leading-snug whitespace-normal ${activeCategory === cat.id ? 'bg-[#5B6B3F] text-white shadow-[0_10px_24px_rgba(91,107,63,0.22)]' : 'border-[#EBE8E0] bg-white text-[#7A7A75] hover:border-[#5B6B3F] hover:text-[#5B6B3F]'}`}
                  onClick={() => setActiveCategory(cat.id)}
                  data-testid={`category-${cat.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {getCategoryDisplayName(cat.name)}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Dishes Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(dish => (
            <div key={dish.id} className="dish-card bg-white rounded-2xl overflow-hidden border border-[#EBE8E0]" data-testid={`dish-card-${dish.id}`}>
              {dish.image && (
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={getPublicImageUrl(dish.image)} alt={dish.name} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-heading text-lg font-semibold text-[#2A2A28]">{dish.name}</h3>
                  <span className="text-[#5B6B3F] font-bold whitespace-nowrap">
                    {(dish.prices?.[priceKey] || 0).toFixed(2)} EUR
                  </span>
                </div>
                <p className="text-sm text-[#7A7A75] mb-3 line-clamp-2">{dish.description}</p>
                {dish.dish_type === 'formula' && (
                  <p className="text-xs text-[#5B6B3F] mb-3">
                    Formule a composer par le client
                  </p>
                )}
                {dish.allergens && (
                  <div className="flex gap-1 flex-wrap mb-3">
                    {dish.allergens.split(',').map((a, i) => (
                      <Badge key={i} variant="outline" className="text-xs border-[#D4AF37]/30 text-[#D4AF37] bg-[#D4AF37]/5">{a.trim()}</Badge>
                    ))}
                  </div>
                )}
                {(orderMode) && (
                  <Button
                    disabled={!!settings?.exceptional_closure_enabled}
                    className="w-full rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white mt-2"
                    onClick={() => handleAdd(dish)}
                    data-testid={`add-to-cart-${dish.id}`}
                  >
                    <Plus className="w-4 h-4 mr-1" /> {dish.dish_type === 'formula' ? 'Composer la formule' : 'Ajouter au panier'}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-[#7A7A75]">Aucun plat trouve.</div>
        )}

        {/* Floating Cart Button */}
        {orderMode && itemCount > 0 && (
          <a href="/cart" className="fixed bottom-6 right-6 z-40" data-testid="floating-cart">
            <Button className="rounded-full bg-[#5B6B3F] hover:bg-[#4A5832] text-white shadow-lg px-6 h-12 text-base">
              <ShoppingBag className="w-5 h-5 mr-2" /> Panier ({itemCount})
            </Button>
          </a>
        )}
      </div>

      <Dialog open={!!formulaDish} onOpenChange={() => setFormulaDish(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{formulaDish?.name}</DialogTitle>
          </DialogHeader>
          {formulaDish && (
            <div className="space-y-6">
              <p className="text-sm text-[#7A7A75]">{formulaDish.description}</p>
              {formulaDish.formula_groups.map(group => {
                const options = getDishesForGroup(group);
                return (
                  <div key={group.id} className="rounded-2xl border border-[#EBE8E0] p-4">
                    <div className="mb-3">
                      <h3 className="font-semibold text-[#2A2A28]">{group.name}</h3>
                      <p className="text-xs text-[#7A7A75]">
                        {Number(group.selection_min || 0) > 0
                          ? `Choisis au moins ${group.selection_min} element(s)`
                          : 'Choix optionnel'}
                      </p>
                      <p className="text-xs text-[#7A7A75]">
                        {getIncludedSelectionCount(group)} inclus dans la formule. Les choix supplementaires sont factures en plus.
                      </p>
                    </div>
                    <div className="grid gap-2">
                      {options.map(option => {
                        const choiceCount = getFormulaChoiceCount(group.id, option.id);
                        return (
                          <div
                            key={option.id}
                            className={`rounded-xl border px-4 py-3 transition ${
                              choiceCount > 0
                                ? 'border-[#5B6B3F] bg-[#5B6B3F]/8 text-[#2A2A28]'
                                : 'border-[#EBE8E0] bg-white text-[#2A2A28]'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-medium">{option.name}</p>
                                {getOptionConfiguredExtra(group, option) > 0 ? (
                                  <p className="text-xs text-[#D4A840]">Inclus avec supplement +{getOptionConfiguredExtra(group, option).toFixed(2)} EUR</p>
                                ) : (
                                  <p className="text-xs text-[#7A7A75]">Inclus sans supplement</p>
                                )}
                                {getOptionExtraCount(group, option.id) > 0 && (
                                  <p className="text-xs text-[#D4A840]">
                                    +{Number(option.prices?.[priceKey] || 0).toFixed(2)} EUR x {getOptionExtraCount(group, option.id)} choix en plus
                                  </p>
                                )}
                                {choiceCount > 0 && <p className="text-xs text-[#5B6B3F]">Choisi {choiceCount} fois</p>}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8 rounded-full border-[#EBE8E0]"
                                  onClick={() => removeFormulaChoice(group, option)}
                                  disabled={choiceCount === 0}
                                >
                                  <Minus className="w-3.5 h-3.5" />
                                </Button>
                                <Badge className="min-w-8 justify-center bg-[#F1EBDD] text-[#2A2A28] hover:bg-[#F1EBDD]">
                                  {choiceCount}
                                </Badge>
                                <Button
                                  type="button"
                                  size="icon"
                                  className="h-8 w-8 rounded-full bg-[#5B6B3F] hover:bg-[#4A5832] text-white"
                                  onClick={() => addFormulaChoice(group, option)}
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {options.length === 0 && (
                        <p className="text-sm text-[#8C3A3A]">
                          Aucun plat configure pour cette sous-categorie.
                        </p>
                      )}
                    </div>
                    {getGroupSurchargeTotal(group) > 0 && (
                      <p className="mt-3 text-xs font-medium text-[#5B6B3F]">
                        Supplement actuel pour {group.name}: +{getGroupSurchargeTotal(group).toFixed(2)} EUR
                      </p>
                    )}
                  </div>
                );
              })}
              <Button className="w-full rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white" onClick={confirmFormula}>
                Ajouter la formule
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
}
